// Basic setup for a modal with support for buttons that open and close it.
// Basic modal style is in _modal.scss.
import constants from "../../constants";

const openClass = 'open';
const modalOpenClass = 'modal-open';
const artistModalId = 'event-artist-shared-modal';

const setUpModals = () => {
    const htmlTag = document.querySelector('html');
    const appConfig = window.ofcAppConfig || {};
    const artistModalCache = new Map();
    const galleryStates = new Map();
    const disableGalleryModalQuery = window.matchMedia(`(max-width: ${constants.screenWidthBreakpoints.lessThanMd}px)`);
    const canOpenGalleryModal = () => !disableGalleryModalQuery.matches;

    const closeModal = (modalElement) => {
        modalElement.classList.remove(openClass);
        modalElement.setAttribute('aria-hidden', 'true');
        if (!document.querySelector('.modal.open')) {
            htmlTag.classList.remove(modalOpenClass);
        }
    };

    const openModal = (modalElement) => {
        document.querySelectorAll('.modal.open').forEach(closeModal);
        modalElement.classList.add(openClass);
        modalElement.setAttribute('aria-hidden', 'false');
        htmlTag.classList.add(modalOpenClass);
    };

    const handleGalleryViewportChange = () => {
        if (!canOpenGalleryModal()) {
            document.querySelectorAll('.modal.modal--gallery.open').forEach(closeModal);
        }
    };

    if (typeof disableGalleryModalQuery.addEventListener === 'function') {
        disableGalleryModalQuery.addEventListener('change', handleGalleryViewportChange);
    } else if (typeof disableGalleryModalQuery.addListener === 'function') {
        disableGalleryModalQuery.addListener(handleGalleryViewportChange);
    }

    const renderArtistModal = (modalElement, payload, triggerElement) => {
        const imageElement = modalElement.querySelector('[data-artist-modal-image]');
        const nameElement = modalElement.querySelector('[data-artist-modal-name]');
        const subtitleElement = modalElement.querySelector('[data-artist-modal-subtitle]');
        const websiteElement = modalElement.querySelector('[data-artist-modal-website]');
        const bioElement = modalElement.querySelector('[data-artist-modal-bio]');
        const subtitle = (triggerElement?.getAttribute('data-artist-subtitle') || '').trim();
        const isLoading = !payload;

        const imageWrap = imageElement?.closest('.modal-image-wrap');
        const leftPanel = modalElement.querySelector('.modal-left');

        modalElement.querySelectorAll('.skeleton-bone').forEach(el => el.remove());

        if (imageElement) {
            imageElement.src = isLoading ? '' : (payload.headshot?.url || '');
            imageElement.alt = isLoading ? '' : (payload.headshot?.alt || '');
            imageElement.style.objectPosition = isLoading ? '' : (payload.headshot?.objectPosition || 'center center');
        }

        if (isLoading && imageWrap) {
            imageWrap.insertAdjacentHTML('beforeend', '<div class="skeleton-bone"></div>');
        }

        if (nameElement) {
            nameElement.textContent = isLoading ? '' : (payload.artistName || triggerElement?.getAttribute('data-artist-name') || '');
        }

        if (subtitleElement) {
            subtitleElement.textContent = isLoading ? '' : subtitle;
            subtitleElement.hidden = isLoading || subtitle === '';
        }

        if (isLoading && leftPanel) {
            leftPanel.insertAdjacentHTML('beforeend',
                '<div class="skeleton-bone skeleton-bone--name"></div>' +
                '<div class="skeleton-bone skeleton-bone--subtitle"></div>'
            );
        }

        if (websiteElement) {
            websiteElement.href = '';
            websiteElement.hidden = true;
            if (!isLoading && payload.websiteHref) {
                websiteElement.href = payload.websiteHref;
                websiteElement.hidden = false;
            }
        }

        if (bioElement) {
            if (isLoading) {
                bioElement.innerHTML =
                    '<div class="skeleton-bone"></div>' +
                    '<div class="skeleton-bone"></div>' +
                    '<div class="skeleton-bone"></div>' +
                    '<div class="skeleton-bone"></div>';
            } else {
                bioElement.innerHTML = payload.bioHtml || '';
            }
        }
    };

    const fetchArtistModalPayload = async (artistId) => {
        if (!artistId || !appConfig.ajaxUrl || !appConfig.artistModalNonce) {
            throw new Error('Missing artist modal config');
        }

        if (artistModalCache.has(artistId)) {
            return artistModalCache.get(artistId);
        }

        const requestBody = new URLSearchParams({
            action: 'ofc_get_artist_modal',
            artistId,
            nonce: appConfig.artistModalNonce,
        });

        const response = await fetch(appConfig.ajaxUrl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
            },
            body: requestBody.toString(),
        });

        if (!response.ok) {
            throw new Error(`Artist modal request failed with status ${response.status}`);
        }

        const payload = await response.json();
        if (!payload?.success || !payload?.data) {
            throw new Error(payload?.data?.message || 'Artist modal request failed');
        }

        artistModalCache.set(artistId, payload.data);
        return payload.data;
    };

    // open buttons
    document.querySelectorAll('.modal-opener').forEach(function (modalOpenerElement) {
        if (modalOpenerElement.hasAttribute('data-artist-modal-trigger')) {
            return;
        }

        const modalId = modalOpenerElement.getAttribute('data-modal-id');
        const modalElement = document.querySelector(`.modal[data-modal-id="${modalId}"]`);
        if (modalElement) {
            modalOpenerElement.addEventListener("click", function (event) {
                event.preventDefault();
                openModal(modalElement);
            });
        }
    });

    const sharedArtistModal = document.querySelector(`.modal[data-modal-id="${artistModalId}"]`);
    document.querySelectorAll('[data-artist-modal-trigger]').forEach((triggerElement) => {
        if (!sharedArtistModal) {
            return;
        }

        triggerElement.addEventListener('click', async (event) => {
            event.preventDefault();

            const artistId = (triggerElement.getAttribute('data-artist-id') || '').trim();

            renderArtistModal(sharedArtistModal, null, triggerElement);
            sharedArtistModal.classList.add('is-loading');
            openModal(sharedArtistModal);

            try {
                const payload = await fetchArtistModalPayload(artistId);
                renderArtistModal(sharedArtistModal, payload, triggerElement);
            } catch (error) {
                renderArtistModal(
                    sharedArtistModal,
                    {
                        artistName: triggerElement.getAttribute('data-artist-name') || '',
                        bioHtml: `<p>${appConfig.artistModalError || 'We could not load this artist biography right now.'}</p>`,
                        websiteHref: '',
                        headshot: {
                            url: '',
                            alt: '',
                            objectPosition: 'center center',
                        },
                    },
                    triggerElement
                );
            }

            sharedArtistModal.classList.remove('is-loading');
        });
    });

    // close buttons and backdrop
    document.querySelectorAll('.modal').forEach(function (modalElement) {
        modalElement.addEventListener("click", function (event) {
            if (event.target.closest('.modal-closer')) {
                closeModal(modalElement);
            }
        });
    });

    // ESC closes currently open modal
    document.addEventListener("keydown", function (event) {
        const openGalleryModal = document.querySelector('.modal.modal--gallery.open');

        if (openGalleryModal && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
            const state = galleryStates.get(openGalleryModal.dataset.modalId);
            if (state && state.images.length > 1) {
                event.preventDefault();
                const nextIndex = event.key === 'ArrowRight'
                    ? (state.currentIndex + 1) % state.images.length
                    : (state.currentIndex - 1 + state.images.length) % state.images.length;
                state.currentIndex = nextIndex;
                renderGalleryModal(openGalleryModal, state);
            }
            return;
        }

        if (event.key === 'Escape') {
            document.querySelectorAll('.modal.open').forEach(closeModal);
        }
    });
    
    const renderGalleryModal = (modalElement, state) => {
        const activeItem = state.images[state.currentIndex];
        if (!activeItem) {
            return;
        }

        const imageElement = modalElement.querySelector('[data-gallery-modal-image]');
        const captionElement = modalElement.querySelector('[data-gallery-modal-caption]');
        const prevButton = modalElement.querySelector('[data-gallery-prev]');
        const nextButton = modalElement.querySelector('[data-gallery-next]');

        if (imageElement) {
            imageElement.src = activeItem.src;
            imageElement.alt = activeItem.alt || '';
        }

        if (captionElement) {
            captionElement.textContent = activeItem.caption || '';
            captionElement.classList.toggle('is-empty', !activeItem.caption);
        }

        const hasMultiple = state.images.length > 1;
        if (prevButton) {
            prevButton.disabled = !hasMultiple;
        }
        if (nextButton) {
            nextButton.disabled = !hasMultiple;
        }
    };

    document.querySelectorAll('.flexible-gallery[data-gallery-modal-id]').forEach((galleryElement) => {
        const modalId = galleryElement.dataset.galleryModalId;
        const modalElement = document.querySelector(`.modal[data-modal-id="${modalId}"]`);
        if (!modalElement) {
            return;
        }

        const images = Array.from(galleryElement.querySelectorAll('.flexible-gallery__trigger')).map((buttonElement) => ({
            src: buttonElement.dataset.galleryImage || '',
            alt: buttonElement.dataset.galleryAlt || '',
            caption: buttonElement.dataset.galleryCaption || '',
        })).filter((image) => image.src);

        if (!images.length) {
            return;
        }

        const state = {
            images,
            currentIndex: 0,
        };
        galleryStates.set(modalId, state);

        galleryElement.querySelectorAll('.flexible-gallery__trigger').forEach((buttonElement) => {
            buttonElement.addEventListener('click', (event) => {
                if (!canOpenGalleryModal()) {
                    return;
                }
                event.preventDefault();
                const index = Number.parseInt(buttonElement.dataset.galleryIndex || '0', 10);
                state.currentIndex = Number.isNaN(index) ? 0 : Math.max(0, Math.min(index, state.images.length - 1));
                renderGalleryModal(modalElement, state);
                openModal(modalElement);
            });
        });

        const prevButton = modalElement.querySelector('[data-gallery-prev]');
        const nextButton = modalElement.querySelector('[data-gallery-next]');

        if (prevButton) {
            prevButton.addEventListener('click', () => {
                if (state.images.length < 2) {
                    return;
                }
                state.currentIndex = (state.currentIndex - 1 + state.images.length) % state.images.length;
                renderGalleryModal(modalElement, state);
            });
        }

        if (nextButton) {
            nextButton.addEventListener('click', () => {
                if (state.images.length < 2) {
                    return;
                }
                state.currentIndex = (state.currentIndex + 1) % state.images.length;
                renderGalleryModal(modalElement, state);
            });
        }
    });
};

export default setUpModals;
